import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { VisitService } from "@/services/visitService";
import type { VisitWithDetails, VisitFormData } from "@/types/visit";
import type { Step } from "@/types/task";
import { TbPlus, TbTrash, TbClock, TbCalendar, TbNotes } from "react-icons/tb";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useVisitsRealtime } from "@/hooks/useVisitsRealtime";
import { useTenant } from "@/hooks/useTenant";

interface TaskVisitsProps {
  taskId: string;
  steps?: Step[];
  isViewMode?: boolean;
}

export const TaskVisits: React.FC<TaskVisitsProps> = ({
  taskId,
  steps = [],
  isViewMode = false,
}) => {
  const [showAddVisit, setShowAddVisit] = useState(false);
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  // Enable real-time updates for visits
  useVisitsRealtime(taskId);

  // Fetch visits
  const { data: visits = [], isLoading } = useQuery({
    queryKey: ["task-visits", taskId],
    queryFn: () => VisitService.getTaskVisits(taskId, tenantId!),
    enabled: !!taskId && !!tenantId,
  });

  // Fetch visit stats
  const { data: stats } = useQuery({
    queryKey: ["task-visit-stats", taskId],
    queryFn: () => VisitService.getTaskVisitStats(taskId, tenantId!),
    enabled: !!taskId && !!tenantId,
  });

  // Create visit mutation
  const createVisitMutation = useMutation({
    mutationFn: (visitData: VisitFormData) =>
      VisitService.createVisit(visitData, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-visits", taskId] });
      queryClient.invalidateQueries({ queryKey: ["task-visit-stats", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      setShowAddVisit(false);
      toast.success("Visit added successfully");
    },
    onError: (error: Error) => {
      toast.error(error?.message || "Failed to add visit");
    },
  });

  // Delete visit mutation
  const deleteVisitMutation = useMutation({
    mutationFn: (visitId: string) =>
      VisitService.deleteVisit(visitId, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-visits", taskId] });
      queryClient.invalidateQueries({ queryKey: ["task-visit-stats", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("Visit deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error?.message || "Failed to delete visit");
    },
  });

  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-500">Loading visits...</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Visit Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#EEF2FF] rounded-[10px] p-4">
            <div className="text-[24px] font-bold text-[#374795] font-['Open_Sans']">
              {stats.total_visits}
            </div>
            <div className="text-[12px] text-[#828282] font-['Open_Sans']">
              Total Visits
            </div>
          </div>
          <div className="bg-[#F0FDF4] rounded-[10px] p-4">
            <div className="text-[24px] font-bold text-[#16A34A] font-['Open_Sans']">
              {stats.unique_visitors}
            </div>
            <div className="text-[12px] text-[#828282] font-['Open_Sans']">
              Unique Visitors
            </div>
          </div>
          <div className="bg-[#F5F3FF] rounded-[10px] p-4">
            <div className="text-[24px] font-bold text-[#7C3AED] font-['Open_Sans']">
              {Math.round(stats.total_duration_minutes / 60) || 0}h
            </div>
            <div className="text-[12px] text-[#828282] font-['Open_Sans']">
              Total Time
            </div>
          </div>
          <div className="bg-[#FFF7ED] rounded-[10px] p-4">
            <div className="text-[14px] font-bold text-[#EA580C] font-['Open_Sans']">
              {stats.last_visit
                ? format(
                    toZonedTime(
                      new Date(stats.last_visit),
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    ),
                    "MMM d, yyyy h:mm a"
                  )
                : "N/A"}
            </div>
            <div className="text-[12px] text-[#828282] font-['Open_Sans']">
              Last Visit
            </div>
          </div>
        </div>
      )}

      {/* Add Visit Button */}
      {!isViewMode && !showAddVisit && (
        <button
          type="button"
          onClick={() => setShowAddVisit(true)}
          className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-[#374795] text-white rounded-lg hover:bg-[#2d3975] transition-colors font-medium"
        >
          <TbPlus className="w-5 h-5" />
          Add Visit
        </button>
      )}

      {/* Add/Edit Visit Form */}
      {showAddVisit && (
        <AddVisitForm
          taskId={taskId}
          steps={steps}
          onSubmit={(data) => createVisitMutation.mutate(data)}
          onCancel={() => setShowAddVisit(false)}
          isSubmitting={createVisitMutation.isPending}
        />
      )}

      {/* Visits List */}
      <div className="space-y-4">
        {visits.length === 0 ? (
          <div className="text-center py-12 text-[#828282]">
            <TbCalendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-[16px] font-semibold font-['Open_Sans']">
              No visits recorded yet
            </p>
            <p className="text-[14px] mt-2 font-['Open_Sans']">
              Add your first visit to track work on this task
            </p>
          </div>
        ) : (
          visits.map((visit) => (
            <VisitCard
              key={visit.id}
              visit={visit}
              onDelete={() => {
                if (confirm("Are you sure you want to delete this visit?")) {
                  deleteVisitMutation.mutate(visit.id);
                }
              }}
              isViewMode={isViewMode}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Visit Card Component
const VisitCard: React.FC<{
  visit: VisitWithDetails;
  onDelete: () => void;
  isViewMode: boolean;
}> = ({ visit, onDelete, isViewMode }) => {
  const resolvedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localVisitDate = toZonedTime(
    new Date(visit.visited_at),
    resolvedTimezone
  );
  const formattedVisitDate = format(localVisitDate, "MMM d, yyyy 'at' h:mm a");

  return (
    <div className="flex flex-col gap-4 p-5 rounded-2xl border border-[#F3F4F6] outline-1 outline-[#F3F4F6] -outline-offset-1 bg-white shadow-[0px_0px_24px_0px_rgba(0,0,0,0.04)] hover:shadow-[0px_0px_32px_0px_rgba(0,0,0,0.08)] transition-shadow">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <div className="text-[#444444] text-[16px] font-['Open_Sans'] font-bold leading-tight">
              {visit.user_name}
            </div>
            <div className="text-[#828282] text-[12px] font-['Open_Sans']">
              Site Visit
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F3F4F6] rounded-[20px]">
              <TbCalendar className="w-4 h-4 text-[#828282]" />
              <span className="text-[12px] font-semibold text-[#444444] font-['Open_Sans']">
                {formattedVisitDate}
              </span>
            </div>
            {visit.duration_minutes && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FEF3C7] rounded-[20px] text-[#92400E]">
                <TbClock className="w-4 h-4" />
                <span className="text-[12px] font-semibold font-['Open_Sans']">
                  {visit.duration_minutes} min
                </span>
              </div>
            )}
          </div>
        </div>

        {!isViewMode && (
          <button
            type="button"
            onClick={onDelete}
            className="cursor-pointer w-10 h-10 rounded-full border border-[#F3F4F6] text-red-500 hover:bg-red-50 flex items-center justify-center shrink-0 transition-colors"
            title="Delete visit"
          >
            <TbTrash className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Steps worked */}
      {visit.steps_worked && visit.steps_worked.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-[12px] font-semibold text-[#828282] uppercase tracking-wide font-['Open_Sans']">
            Steps Worked
          </div>
          <div className="flex flex-wrap gap-2">
            {visit.steps_worked.map((step) => (
              <span
                key={step.step_id}
                className={`px-2.5 py-[3px] pb-[5px] rounded-[20px] text-[12px] font-['Open_Sans'] leading-[15.6px] font-semibold ${
                  step.is_primary
                    ? "bg-[rgba(55,71,149,0.08)] text-[#374795]"
                    : "bg-[#F3F4F6] text-[#444444]"
                }`}
              >
                {step.step_title}
                {step.is_primary && " • Primary"}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {visit.notes && (
        <div className="flex flex-col gap-2">
          <div className="text-[12px] font-semibold text-[#828282] uppercase tracking-wide font-['Open_Sans']">
            Notes
          </div>
          <div className="flex items-start gap-3 p-4 rounded-2xl border border-[#F3F4F6] bg-[#FDFDFD]">
            <div className="w-10 h-10 rounded-full bg-[#EEF2FF] flex items-center justify-center text-[#374795]">
              <TbNotes className="w-5 h-5" />
            </div>
            <p className="text-[14px] text-[#444444] font-['Open_Sans'] leading-relaxed whitespace-pre-wrap">
              {visit.notes}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Add Visit Form Component
const AddVisitForm: React.FC<{
  taskId: string;
  steps: Step[];
  onSubmit: (data: VisitFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}> = ({ taskId, steps, onSubmit, onCancel, isSubmitting }) => {
  // Helper function to get local datetime string for datetime-local input
  const getLocalDateTimeString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState<VisitFormData>({
    task_id: taskId,
    visited_at: getLocalDateTimeString(),
    notes: "",
    duration_minutes: undefined,
    step_ids: [],
    primary_step_id: undefined,
  });

  const handleSubmit = () => {
    // datetime-local gives us a string like "2025-12-02T14:30"
    // This is already in local time, so we just need to append seconds and convert to ISO
    const visitData = {
      ...formData,
      visited_at: formData.visited_at.includes("T")
        ? new Date(formData.visited_at).toISOString()
        : new Date(formData.visited_at + ":00").toISOString(),
    };

    onSubmit(visitData);
  };

  const toggleStep = (stepId: string) => {
    setFormData((prev) => {
      const step_ids = prev.step_ids || [];
      const newStepIds = step_ids.includes(stepId)
        ? step_ids.filter((id) => id !== stepId)
        : [...step_ids, stepId];

      return {
        ...prev,
        step_ids: newStepIds,
        primary_step_id:
          newStepIds.length === 1 ? newStepIds[0] : prev.primary_step_id,
      };
    });
  };

  return (
    <div className="border border-[#E5E7EB] rounded-[10px] p-4 bg-[#F3F4F6]">
      <h3 className="text-[18px] font-bold mb-4 text-[#444444] font-['Open_Sans']">
        Add Visit
      </h3>
      <div className="space-y-4">
        {/* Date & Time */}
        <div>
          <label className="block text-[14px] font-semibold text-[#444444] mb-1 font-['Open_Sans']">
            Date & Time
          </label>
          <input
            type="datetime-local"
            value={formData.visited_at}
            onChange={(e) =>
              setFormData({ ...formData, visited_at: e.target.value })
            }
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-[10px] focus:ring-2 focus:ring-[#374795] focus:border-[#374795] bg-white text-[#444444] text-[14px] font-['Open_Sans']"
            required
          />
        </div>

        {/* Duration */}
        <div>
          <label className="block text-[14px] font-semibold text-[#444444] mb-1 font-['Open_Sans']">
            Duration (minutes)
          </label>
          <input
            type="number"
            value={formData.duration_minutes || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                duration_minutes: e.target.value
                  ? parseInt(e.target.value)
                  : undefined,
              })
            }
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-[10px] focus:ring-2 focus:ring-[#374795] focus:border-[#374795] bg-white text-[#444444] text-[14px] font-['Open_Sans'] placeholder:text-[#828282]"
            min="1"
            placeholder="Optional"
          />
        </div>

        {/* Steps */}
        {steps.length > 0 && (
          <div>
            <label className="block text-[14px] font-semibold text-[#444444] mb-2 font-['Open_Sans']">
              Steps Worked On
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {steps.map((step) => (
                <label
                  key={step.id}
                  className="flex items-center gap-2 p-2 hover:bg-white rounded-xl cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.step_ids?.includes(step.id) || false}
                    onChange={() => toggleStep(step.id)}
                    className="w-4 h-4 text-[#374795] rounded focus:ring-[#374795]"
                  />
                  <span className="text-[14px] font-['Open_Sans'] text-[#444444]">
                    {step.step_name}
                  </span>
                  {formData.step_ids?.includes(step.id) &&
                    formData.step_ids.length > 1 && (
                      <input
                        type="radio"
                        name="primary_step"
                        value={step.id}
                        checked={formData.primary_step_id === step.id}
                        onChange={() =>
                          setFormData({ ...formData, primary_step_id: step.id })
                        }
                        className="ml-auto"
                        title="Primary step"
                      />
                    )}
                </label>
              ))}
            </div>
            {formData.step_ids && formData.step_ids.length > 1 && (
              <p className="text-[12px] text-[#828282] mt-1 font-['Open_Sans']">
                Select primary step using radio buttons
              </p>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-[14px] font-semibold text-[#444444] mb-1 font-['Open_Sans']">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-[10px] focus:ring-2 focus:ring-[#374795] focus:border-[#374795] resize-none bg-white text-[#444444] text-[14px] font-['Open_Sans'] placeholder:text-[#828282]"
            rows={3}
            placeholder="What did you work on?"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer px-4 py-2 text-[#444444] bg-white border border-[#E5E7EB] rounded-[10px] hover:bg-[#F3F4F6] transition-colors font-['Open_Sans'] font-semibold text-[14px]"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="cursor-pointer px-4 py-2 bg-[#374795] text-white rounded-[10px] hover:bg-[#2d3975] transition-colors disabled:opacity-50 font-['Open_Sans'] font-semibold text-[14px]"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Visit"}
          </button>
        </div>
      </div>
    </div>
  );
};
